import { useEffect, useMemo, useState } from 'react';
import { useOutletContext, Link } from 'react-router-dom';
import type {
  ApiClient, Huellenbaum as Baumdaten, Huellenart, Huellentyp, Huellenfeld,
  Huellenfeldwert, Huellenbauteil,
} from '../api';
import { IconEbene, IconRaum, IconSuche, IconOk, IconWarnung } from '../Icons';
import { PageHeader } from '../rahmen/PageHeader';
import {
  ContentSection, DataTable, EmptyState, ErrorState, KpiCard, KpiReihe,
  LoadingState, StatusBadge, WertEinheit, type Spalte,
} from '../muster';

/**
 * Die Bauteilarten der Gebäudehülle — Gruppe → Bauteilart → Typ.
 *
 * --- Der Aufbau folgt Heizung und Kälte ----------------------------------------
 *
 * Links die Gliederung, rechts der gewählte Knoten. Dieselbe Entscheidung und
 * derselbe Grund: Ein Baum über die ganze Breite mit dem Inhalt darunter zwingt
 * zum Rollen zwischen Auswahl und Anzeige.
 *
 * --- Warum der TYP die Zeile ist ------------------------------------------------
 *
 * 16.408 Hüllbauteile stehen 635 Typen gegenüber. Der U-Wert ist eine Eigenschaft
 * der Bauart und nicht des einzelnen Wandstücks; wer ihn je Bauteil pflegen
 * müsste, trüge denselben Wert 10.268 Mal ein und wiche dabei irgendwann ab.
 *
 * --- Die Farben ------------------------------------------------------------------
 *
 * Hellgrün: aus dem Modell gelesen, geht bei einem Rückschreiben dorthin zurück.
 * Hellgelb: nur in der Datenbank — hergeleitet, aus dem Nachweis oder von Hand.
 *
 * --- Was hier NICHT steht --------------------------------------------------------
 *
 * Kein Mittelwert über die Bauteile eines Typs. Streuen sie — weil ein Teil aus
 * dem Nachweis kommt und ein Teil aus den Schichten hergeleitet ist —, steht die
 * Spanne da. Ein Mittelwert stünde an keinem einzigen Bauteil und verdeckte
 * genau die Uneinigkeit, die man sehen will.
 */

interface Rahmen { api: ApiClient; projekt?: { id: string } }

/*
 * Welche Felder an einer Bauteilart gelten, entscheidet die API aus
 * huelle_felder.Bauteilarten und liefert es als art.anwendbareFelder mit.
 *
 * Hier stand die Regel einmal nach — und spaltete an ',', während der Katalog
 * mit '|' trennt. Damit traf sie nie, jedes Feld fiel weg, die rechte Seite
 * blieb leer und nichts war zu bearbeiten. Die Regel gehört an genau einen Ort.
 *
 * Der g-Wert erscheint dadurch von selbst nur an Fenster und Vorhangfassade:
 * Der Katalog führt ihn dort und nirgends sonst.
 */

export function Huellenbaum() {
  const { api, projekt } = useOutletContext<Rahmen>();

  const [daten, setDaten] = useState<Baumdaten | null>(null);
  const [offen, setOffen] = useState<Set<string>>(new Set());
  const [gewaehlt, setGewaehlt] = useState<{ art: string; typ: string } | null>(null);
  const [suche, setSuche] = useState('');
  const [nurLuecken, setNurLuecken] = useState(false);
  const [entwurf, setEntwurf] = useState<Record<string, string>>({});
  const [begruendung, setBegruendung] = useState<Record<string, string>>({});
  const [bauteile, setBauteile] = useState<Huellenbauteil[] | null>(null);
  const [ladeBauteile, setLadeBauteile] = useState(false);
  const [speichert, setSpeichert] = useState<string | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  const laden = async () => {
    if (!projekt) return;
    try {
      setDaten(await api.huellenbaum(projekt.id));
      setFehler(null);
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    }
  };

  useEffect(() => { void laden(); }, [projekt?.id]);

  /** Ein Typ hat eine Lücke, wenn nicht jedes Bauteil einen U-Wert trägt. */
  const hatLuecke = (t: Huellentyp) => {
    const u = t.felder.find((f) => f.feld === 'thermal_u_value_w_m2k');
    return !u || u.mitWert < t.anzahl;
  };

  const gefiltert = useMemo(() => {
    // `?? []` an jeder Sammlung: Eine Antwort ohne `arten` brach die Seite ab,
    // statt den Leerzustand zu zeigen (gemessen im Prüfstand, Paket U6b).
    if (!daten?.arten) return [];
    const s = suche.trim().toLowerCase();

    return daten.arten
      .map((a) => ({
        ...a,
        typen: a.typen.filter(
          (t) => (!s || t.typ.toLowerCase().includes(s)
                    || (t.bauteilcode ?? '').toLowerCase().includes(s))
              && (!nurLuecken || hatLuecke(t)),
        ),
      }))
      .filter((a) => a.typen.length > 0);
  }, [daten, suche, nurLuecken]);

  /** Die Gliederung der obersten Ebene kommt aus der Katalogspalte Gruppe. */
  const gruppen = useMemo(() => {
    const m = new Map<string, Huellenart[]>();
    for (const a of gefiltert) {
      if (!m.has(a.gruppe)) m.set(a.gruppe, []);
      m.get(a.gruppe)!.push(a);
    }
    return [...m.entries()];
  }, [gefiltert]);

  const knoten = useMemo(() => {
    if (!daten?.arten || !gewaehlt) return null;
    const art = daten.arten.find((a) => a.schluessel === gewaehlt.art);
    const typ = art?.typen.find((t) => t.typ === gewaehlt.typ);
    return art && typ ? { art, typ } : null;
  }, [daten, gewaehlt]);

  // Die Bauteile eines Typs erst bei der Auswahl holen. Alle 16.408 im Baum
  // mitzuliefern wären mehrere Megabyte, von denen immer nur die eines Typs
  // angesehen werden.
  useEffect(() => {
    if (!projekt || !gewaehlt) { setBauteile(null); return; }

    let abgebrochen = false;
    setLadeBauteile(true);
    setBauteile(null);

    api.huelleBauteileEinesTyps(projekt.id, gewaehlt.art, gewaehlt.typ)
      .then((x) => { if (!abgebrochen) setBauteile(x); })
      .catch((e) => {
        if (!abgebrochen)
          setFehler(e instanceof Error ? e.message : String(e));
      })
      .finally(() => { if (!abgebrochen) setLadeBauteile(false); });

    // Ohne das Abbrechen setzt eine langsamere frühere Antwort die Liste
    // eines Typs, der längst nicht mehr gewählt ist.
    return () => { abgebrochen = true; };
  }, [projekt?.id, gewaehlt?.art, gewaehlt?.typ]);

  const setzen = async (feld: string) => {
    if (!projekt || !knoten) return;

    const roh = (entwurf[feld] ?? '').trim();
    const zahl = roh === '' ? null : Number(roh.replace(',', '.'));

    if (zahl !== null && !Number.isFinite(zahl)) {
      setFehler(`„${roh}" ist keine Zahl.`);
      return;
    }

    setSpeichert(feld);
    setFehler(null);

    try {
      const e = await api.huelleTypwertSetzen(projekt.id, {
        bauteilart: knoten.art.schluessel,
        typ: knoten.typ.typ,
        feldschluessel: feld,
        zahlwert: zahl,
        begruendung: (begruendung[feld] ?? '').trim() || null,
      });

      setMeldung(
        zahl === null
          ? `Festlegung entfernt — ${e.abgeraeumt} Bauteilwert(e) abgeräumt, `
            + 'die Herleitung greift wieder.'
          : `Gesetzt an ${e.gesetzt + e.geaendert} Bauteil(en)`
            + (e.abgeraeumt ? `, ${e.abgeraeumt} abgeräumt` : '') + '.',
      );

      setEntwurf((x) => { const n = { ...x }; delete n[feld]; return n; });
      setBegruendung((x) => { const n = { ...x }; delete n[feld]; return n; });
      await laden();

      // Die Bauteilliste zeigt den U-Wert je Bauteil - sie muss den neuen
      // Stand sehen, sonst steht dort weiter der alte.
      if (projekt && knoten)
        setBauteile(await api.huelleBauteileEinesTyps(
          projekt.id, knoten.art.schluessel, knoten.typ.typ));
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setSpeichert(null);
    }
  };

  if (!projekt) return <LoadingState text="Projekt wird geladen …" />;

  return (
    <>
      <PageHeader
        titel="Bauteilarten der Hülle"
        beschreibung={'Gruppe → Bauteilart → Typ. Der Kennwert wird am Typ gepflegt '
          + 'und auf alle seine Bauteile ausgetragen — 16.408 Hüllbauteile stehen '
          + '635 Typen gegenüber.'}
        meta={(
          <Link to={`/projekte/${projekt.id}/huelle`}>
            ← Datenstand und Prüfungen
          </Link>
        )}
      />

      {fehler && (
        <ErrorState
          meldung="Die Bauteilarten der Hülle konnten nicht geladen werden."
          technisch={fehler}
          beiErneutVersuchen={() => { setFehler(null); void laden(); }}
        />
      )}
      {meldung && <div className="karte hinweis">{meldung}</div>}

      {!daten ? (
        <LoadingState text="Bauteilarten werden geladen …" zeilen={10} spalten={4} />
      ) : (daten.arten ?? []).length === 0 ? (
        <EmptyState
          titel="Für dieses Modell ist noch kein Hüllenbestand aufgenommen."
          erklaerung={'Er entsteht aus einem Auftrag mit Hüllenaufnahme — unter '
            + '„Gebäudehülle“.'}
          aktion={(
            <Link to={`/projekte/${projekt.id}/huelle`} className="m-knopf m-knopf-haupt" style={{ textDecoration: 'none' }}>
              Zur Gebäudehülle
            </Link>
          )}
        />
      ) : (
        <>
          <Kennzahlen arten={daten.arten ?? []} />

          <div className="karte" style={{ display: 'flex', gap: 0, padding: 0 }}>
            {/* Links die Gliederung, rechts der gewählte Typ. */}
            <div className="baum">
              <div style={{ padding: '10px 12px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <IconSuche size={14} />
                  <input
                    value={suche}
                    onChange={(e) => setSuche(e.target.value)}
                    placeholder="Typ oder Bauteilcode"
                    style={{ width: '100%', padding: '4px 6px', fontSize: 13 }}
                  />
                </label>

                <label style={{ display: 'flex', alignItems: 'center', gap: 6,
                                marginTop: 8, fontSize: 12 }}>
                  <input
                    type="checkbox"
                    checked={nurLuecken}
                    onChange={(e) => setNurLuecken(e.target.checked)}
                  />
                  nur Typen mit Lücke
                </label>
              </div>

              {gruppen.map(([gruppe, arten]) => (
                <div key={gruppe}>
                  <div className="baum-ebene" style={{ cursor: 'default',
                                                       fontWeight: 600 }}>
                    <span>{gruppe}</span>
                  </div>

                  {arten.map((a) => (
                    <div key={a.schluessel}>
                      <button
                        className="baum-ebene"
                        style={{ paddingLeft: 20 }}
                        onClick={() => setOffen((s) => {
                          const n = new Set(s);
                          if (n.has(a.schluessel)) n.delete(a.schluessel);
                          else n.add(a.schluessel);
                          return n;
                        })}
                        title={a.hinweis ?? a.bezeichnung}
                      >
                        <IconEbene size={14} />
                        <span>{a.bezeichnung}</span>
                        <span className="schwach" style={{ marginLeft: 'auto' }}>
                          {a.typen.length}
                        </span>
                      </button>

                      {offen.has(a.schluessel) && a.typen.map((t) => (
                        <button
                          key={t.typ}
                          className={`baum-zeile ${
                            gewaehlt?.art === a.schluessel && gewaehlt?.typ === t.typ
                              ? 'aktiv' : ''}`}
                          style={{ paddingLeft: 30 }}
                          onClick={() => {
                            setGewaehlt({ art: a.schluessel, typ: t.typ });
                            setEntwurf({});
                            setBegruendung({});
                            setMeldung(null);
                          }}
                          title={`${t.typ} — ${t.anzahl} Bauteil(e), `
                               + `${t.flaecheM2.toLocaleString('de-DE')} m²`}
                        >
                          <IconRaum size={13} />

                          <span style={{ minWidth: 0, overflow: 'hidden',
                                         textOverflow: 'ellipsis',
                                         whiteSpace: 'nowrap' }}>
                            {t.typ}
                          </span>

                          <span style={{ marginLeft: 'auto', display: 'flex',
                                         gap: 4, fontSize: 10 }}>
                            <span className="schwach">{t.anzahl}</span>
                            {hatLuecke(t)
                              ? <IconWarnung size={12} />
                              : <IconOk size={12} />}
                          </span>
                        </button>
                      ))}
                    </div>
                  ))}
                </div>
              ))}
            </div>

            <div style={{ flex: 1, padding: '12px 16px', minWidth: 0 }}>
              {!knoten ? (
                <EmptyState
                  klein
                  titel="Noch kein Typ gewählt."
                  erklaerung={'Links eine Bauteilart aufklappen und einen Typ '
                    + 'wählen. Das Warnzeichen steht an Typen, bei denen nicht '
                    + 'jedes Bauteil einen U-Wert trägt.'}
                />
              ) : (
                <Typkarte
                  art={knoten.art}
                  typ={knoten.typ}
                  felder={daten.felder ?? []}
                  entwurf={entwurf}
                  setEntwurf={setEntwurf}
                  begruendung={begruendung}
                  setBegruendung={setBegruendung}
                  speichert={speichert}
                  setzen={setzen}
                  bauteile={bauteile}
                  ladeBauteile={ladeBauteile}
                />
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
}

/** Die Zahlen über dem Baum: wo die Fläche steht, die noch keinen Wert hat. */
function Kennzahlen({ arten }: { arten: Huellenart[] }) {
  const z = useMemo(() => {
    let typen = 0, bauteile = 0, flaeche = 0;
    let ohneU = 0, flaecheOhneU = 0, glas = 0, glasOhneG = 0;

    for (const a of arten) {
      typen += a.typen.length;
      bauteile += a.anzahl;
      flaeche += a.flaecheM2;

      for (const t of a.typen) {
        const u = t.felder.find((f) => f.feld === 'thermal_u_value_w_m2k');
        const fehlend = t.anzahl - (u?.mitWert ?? 0);

        if (fehlend > 0) {
          ohneU += fehlend;
          // Anteilig: Der Typ hat eine Gesamtfläche, und der Teil ohne Wert
          // verhält sich wie der Anteil der Bauteile ohne Wert.
          flaecheOhneU += t.flaecheM2 * (fehlend / t.anzahl);
        }

        if (a.huellart === 'transparent') {
          glas += t.glasflaecheM2;
          const g = t.felder.find((f) => f.feld === 'thermal_g_value');
          if ((g?.mitWert ?? 0) < t.anzahl)
            glasOhneG += t.glasflaecheM2 * ((t.anzahl - (g?.mitWert ?? 0)) / t.anzahl);
        }
      }
    }

    return { typen, bauteile, flaeche, ohneU, flaecheOhneU, glas, glasOhneG };
  }, [arten]);

  const m2 = (x: number) => Math.round(x).toLocaleString('de-DE');

  return (
    <KpiReihe>
      <KpiCard
        titel="Typen" wert={z.typen}
        vergleich={`${z.bauteile.toLocaleString('de-DE')} Bauteile`}
      />
      <KpiCard titel="Hüllfläche" wert={Math.round(z.flaeche)} einheit="m²" />
      <KpiCard
        titel="ohne U-Wert" wert={z.ohneU}
        vergleich={`${m2(z.flaecheOhneU)} m²`}
        zustand={z.ohneU > 0 ? 'warnung' : 'gut'}
        zustandText={z.ohneU > 0 ? 'Lücke im Nachweis' : 'vollständig'}
      />
      <KpiCard
        titel="Glas ohne g-Wert" wert={Math.round(z.glasOhneG)} einheit="m²"
        vergleich={`von ${m2(z.glas)} m²`}
        zustand={z.glasOhneG > 0 ? 'warnung' : 'gut'}
        zustandText={z.glasOhneG > 0 ? 'Lücke im Nachweis' : 'vollständig'}
      />
    </KpiReihe>
  );
}

function Typkarte({
  art, typ, felder, entwurf, setEntwurf, begruendung, setBegruendung,
  speichert, setzen, bauteile, ladeBauteile,
}: {
  art: Huellenart;
  typ: Huellentyp;
  felder: Huellenfeld[];
  entwurf: Record<string, string>;
  setEntwurf: (f: (x: Record<string, string>) => Record<string, string>) => void;
  begruendung: Record<string, string>;
  setBegruendung: (f: (x: Record<string, string>) => Record<string, string>) => void;
  speichert: string | null;
  setzen: (feld: string) => void;
  bauteile: Huellenbauteil[] | null;
  ladeBauteile: boolean;
}) {
  // Die Reihenfolge kommt aus dem Katalog, die Auswahl aus der API.
  const zeigbar = (art.anwendbareFelder ?? [])
    .map((k) => (felder ?? []).find((f) => f.schluessel === k))
    .filter((f): f is Huellenfeld => f !== undefined);

  return (
    <>
      <h3 style={{ marginTop: 0, marginBottom: 2 }}>{typ.typ}</h3>

      <div className="schwach" style={{ fontSize: 12, marginBottom: 12 }}>
        {art.bezeichnung}
        {typ.familie && ` · ${typ.familie}`}
        {typ.bauteilcode && ` · Code ${typ.bauteilcode}`}
        {' · '}
        {typ.anzahl.toLocaleString('de-DE')} Bauteil(e)
        {' · '}
        {typ.flaecheM2.toLocaleString('de-DE')} m²
        {typ.glasflaecheM2 > 0
          && ` · davon ${typ.glasflaecheM2.toLocaleString('de-DE')} m² Glas`}
      </div>

      {/* Der Nachweis. Er steht hier, weil er die Lücke erklärt: Ein Typ ohne
          Verknüpfung bekommt weder U noch g aus dem Wärmeschutznachweis. */}
      {(typ.nachweis ?? []).length > 0 ? (
        <div className="karte datenfeld" style={{ marginBottom: 12, padding: '8px 10px' }}>
          <div className="schwach" style={{ fontSize: 11, marginBottom: 4 }}>
            Wärmeschutznachweis
          </div>
          {typ.nachweis.map((n) => (
            <div key={n.code} style={{ fontSize: 13 }}>
              <strong>{n.code}</strong>
              {n.bezeichnung && <span className="schwach"> {n.bezeichnung}</span>}
              {n.u != null && <span> · U = {n.u.toLocaleString('de-DE')} W/(m²·K)</span>}
              {n.g != null && <span> · g = {n.g.toLocaleString('de-DE')}</span>}
            </div>
          ))}
          {typ.ohneNachweis > 0 && (
            <div className="sehr-schwach" style={{ fontSize: 11, marginTop: 4 }}>
              {typ.ohneNachweis} von {typ.anzahl} Bauteil(en) sind nicht verknüpft
              und bekommen daraus nichts.
            </div>
          )}
        </div>
      ) : (
        <EmptyState
          klein
          titel="Kein Bauteil dieses Typs ist mit dem Wärmeschutznachweis verknüpft."
          erklaerung={'U und g müssen deshalb aus den Schichten hergeleitet oder hier '
            + 'festgelegt werden.'}
        />
      )}

      <table className="tabellenrahmen" style={{ width: '100%' }}>
        <thead>
          <tr>
            <th scope="col" style={{ textAlign: 'left' }}>Kennwert</th>
            <th scope="col" style={{ textAlign: 'right' }}>im Bestand</th>
            <th scope="col" style={{ textAlign: 'left' }}>Herkunft</th>
            <th scope="col" style={{ textAlign: 'left' }}>Festlegung</th>
          </tr>
        </thead>
        <tbody>
          {zeigbar.map((f) => (
            <Feldzeile
              key={f.schluessel}
              feld={f}
              stand={typ.felder.find((x) => x.feld === f.schluessel)}
              anzahl={typ.anzahl}
              entwurf={entwurf[f.schluessel]}
              setEntwurf={(v) => setEntwurf((x) => ({ ...x, [f.schluessel]: v }))}
              begruendung={begruendung[f.schluessel]}
              setBegruendung={(v) => setBegruendung((x) => ({ ...x, [f.schluessel]: v }))}
              speichert={speichert === f.schluessel}
              setzen={() => setzen(f.schluessel)}
            />
          ))}
        </tbody>
      </table>

      <div className="sehr-schwach" style={{ fontSize: 11, marginTop: 10 }}>
        Eine Festlegung gilt für den ganzen Typ und wird sofort auf alle seine
        Bauteile ausgetragen — auch auf die, die beim nächsten Import erst
        dazukommen. Ein leeres Feld entfernt sie wieder; dann greift die
        Herleitung aus den Schichten erneut.
      </div>

      <Bauteilliste bauteile={bauteile} laedt={ladeBauteile} anzahl={typ.anzahl} />
    </>
  );
}

/**
 * Die einzelnen Bauteile des Typs.
 *
 * Sie beantwortet, was die Kennwerttabelle offen laesst: Der Typ hat 18 Bauteile
 * und 16 davon einen U-Wert - welche zwei nicht, und wo stehen sie? Ohne diese
 * Auskunft bleibt "2 von 18 ohne Wert" eine Zahl, mit der niemand ins Modell
 * gehen kann.
 */
function Bauteilliste({ bauteile, laedt, anzahl }: {
  bauteile: Huellenbauteil[] | null;
  laedt: boolean;
  anzahl: number;
}) {
  if (laedt) {
    return <LoadingState text="Bauteile werden geladen …" zeilen={6} spalten={5} />;
  }

  if (!bauteile || bauteile.length === 0) return null;

  // Ohne Wert zuerst - das ist die Arbeitsliste. Der frühere Knopf
  // „alle N zeigen" (Grenze 25) entfällt: `DataTable` virtualisiert (U6b).
  const sortiert = [...bauteile].sort((a, b) => {
    const av = a.uWert == null ? 0 : 1;
    const bv = b.uWert == null ? 0 : 1;
    if (av !== bv) return av - bv;
    return (b.flaecheM2 ?? 0) - (a.flaecheM2 ?? 0);
  });

  const spalten: Spalte<Huellenbauteil>[] = [
    { schluessel: 'geschoss', titel: 'Geschoss', wert: (b) => b.geschoss },
    {
      schluessel: 'bezeichnung', titel: 'Bezeichnung', immerSichtbar: true,
      wert: (b) => b.bezeichnung,
      inhalt: (b) => (
        <span title={b.quellmodell ?? undefined}>
          {b.bezeichnung ?? <span className="sehr-schwach">—</span>}
          {!b.istRaumbegrenzend && (
            <StatusBadge
              zustand="neutral" klein text="nicht raumbegrenzend"
              titel="Geht in keine Raumbilanz ein."
            />
          )}
        </span>
      ),
    },
    {
      schluessel: 'flaeche', titel: 'Fläche', art: 'zahl', einheit: 'm²',
      wert: (b) => b.flaecheM2,
    },
    {
      schluessel: 'u', titel: 'U-Wert', art: 'zahl', einheit: 'W/(m²·K)',
      nachkomma: 2, wert: (b) => b.uWert,
      inhalt: (b) => (b.uWert == null
        ? <StatusBadge zustand="warnung" text="fehlt" klein />
        : <WertEinheit wert={b.uWert} nachkomma={2} rechts />),
    },
    {
      schluessel: 'herkunft', titel: 'Herkunft', art: 'status', breite: '220px',
      wert: (b) => (b.uHerkunft
        ?? (b.schichtenOhneLambda > 0
          ? `${b.schichtenOhneLambda} Schicht(en) ohne λ` : '')),
      inhalt: (b) => (b.uHerkunft
        ? <StatusBadge zustand="info" text={b.uHerkunft} klein />
        : b.schichtenOhneLambda > 0
          ? (
            <StatusBadge
              zustand="warnung" klein
              text={`${b.schichtenOhneLambda} Schicht(en) ohne λ`}
              titel="Die Schichtherleitung bricht ab, solange an einer Schicht die Wärmeleitfähigkeit fehlt."
            />
          )
          : <span className="sehr-schwach">—</span>),
    },
    {
      schluessel: 'element', titel: 'Element',
      wert: (b) => [b.nachweisCode, b.elementId].filter(Boolean).join(' '),
      inhalt: (b) => (
        <span className="sehr-schwach">
          {b.nachweisCode && <span style={{ marginRight: 6 }}>{b.nachweisCode}</span>}
          {b.elementId ?? ''}
        </span>
      ),
    },
  ];

  return (
    <ContentSection
      titel="Bauteile"
      meta={(
        <>
          {bauteile.length.toLocaleString('de-DE')}
          {bauteile.length < anzahl
            && ` von ${anzahl.toLocaleString('de-DE')} — die Liste ist bei 500 abgeschnitten`}
        </>
      )}
      nackt
    >
      <DataTable
        name="Bauteile des Typs"
        spalten={spalten}
        zeilen={sortiert}
        schluessel={(b) => String(b.id)}
        ausfuhr
        ausfuhrDateiname="huellenbauteile"
        dicht
      />
    </ContentSection>
  );
}

function Feldzeile({
  feld, stand, anzahl, entwurf, setEntwurf, begruendung, setBegruendung,
  speichert, setzen,
}: {
  feld: Huellenfeld;
  stand?: Huellenfeldwert;
  anzahl: number;
  entwurf?: string;
  setEntwurf: (v: string) => void;
  begruendung?: string;
  setBegruendung: (v: string) => void;
  speichert: boolean;
  setzen: () => void;
}) {
  const mit = stand?.mitWert ?? 0;
  const fehlt = anzahl - mit;

  const spanne = stand?.min == null ? null
    : stand.min === stand.max
      ? stand.min.toLocaleString('de-DE')
      : `${stand.min.toLocaleString('de-DE')} … ${stand.max!.toLocaleString('de-DE')}`;

  // Hellgrün, sobald ein Wert aus dem Modell dabei ist — er ginge bei einem
  // Rückschreiben dorthin zurück. Sonst hellgelb: nur in der Datenbank.
  const ausModell = (stand?.herkuenfte ?? []).some(
    (h) => h === 'modell' || h === 'typ');

  const bearbeitet = entwurf !== undefined;

  return (
    <tr>
      <td>
        <div title={feld.hinweis ?? undefined}>{feld.bezeichnung}</div>
        {feld.einheit && feld.einheit !== '-' && (
          <div className="sehr-schwach" style={{ fontSize: 11 }}>{feld.einheit}</div>
        )}
      </td>

      <td className={spanne ? (ausModell ? 'modellfeld' : 'datenfeld') : undefined}
          style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
        {spanne ?? <span className="sehr-schwach">—</span>}
        {fehlt > 0 && (
          <div style={{ marginTop: 2 }}>
            <StatusBadge
              zustand="warnung" klein
              text={`${fehlt} von ${anzahl} ohne Wert`}
            />
          </div>
        )}
      </td>

      <td style={{ fontSize: 11 }}>
        {(stand?.herkuenfte ?? []).length === 0
          ? <span className="sehr-schwach">—</span>
          : stand!.herkuenfte.map((h) => (
              <StatusBadge key={h} zustand="info" text={h} klein />
            ))}
      </td>

      <td>
        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          <input
            value={entwurf ?? (stand?.festwert != null
              ? String(stand.festwert).replace('.', ',') : '')}
            onChange={(e) => setEntwurf(e.target.value)}
            placeholder={stand?.festwert != null ? '' : 'nicht festgelegt'}
            style={{ width: 90, padding: '3px 5px', fontSize: 13 }}
          />

          <button
            className={bearbeitet ? 'm-knopf m-knopf-haupt' : 'm-knopf'}
            disabled={speichert || !bearbeitet}
            onClick={setzen}
            style={{ padding: '3px 8px', fontSize: 12 }}
          >
            {speichert ? '…' : 'setzen'}
          </button>
        </div>

        {bearbeitet && (
          <input
            value={begruendung ?? ''}
            onChange={(e) => setBegruendung(e.target.value)}
            placeholder="Begründung — woher stammt der Wert?"
            style={{ width: '100%', marginTop: 4, padding: '3px 5px', fontSize: 12 }}
          />
        )}

        {!bearbeitet && stand?.festBegruendung && (
          <div className="sehr-schwach" style={{ fontSize: 11, marginTop: 2 }}>
            {stand.festBegruendung}
          </div>
        )}

        {!bearbeitet && stand?.festwert != null && (
          <div className="sehr-schwach" style={{ fontSize: 11 }}>
            wirkt an {stand.festAngewendet ?? 0} Bauteil(en)
          </div>
        )}
      </td>
    </tr>
  );
}
